import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ReactFlow,
  ReactFlowProvider,
  SelectionMode,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type IsValidConnection,
  type NodeChange,
  type EdgeChange,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import '@/features/studio/styles/canvas-base.css'
import '@/features/studio/styles/canvas-node-layout.css'
import '@/features/studio/canvas/theme/node-themes.css'
import '@/features/studio/canvas/theme/edge-anims.css'
import { Loader2 } from 'lucide-react'

import { aiApi, extractAudioUrl, extractImageUrl, extractTextOutput, extractVideoUrl } from '@/api/ai'
import {
  getCanvasGraph,
  getProduction,
  getScript,
  listShots,
  registerProductionAsset,
  submitAssetReview,
  submitShotReview,
  type Production,
  type Shot,
  type StudioCanvasGraph,
} from '@/api/studio-production'
import { persistCanvasFile } from '@/features/studio/lib/canvas-media-persist'
import { creativeBasePath, getCreativeGraph, getCreativeWorkspace, listCreativeListings, patchCreativeWorkspace, type CreativeWorkspace, type StudioCreativeListing } from '@/api/studio-creative'
import { CreativeWorkspaceListingModal } from '@/features/studio/modals/CreativeWorkspaceListingModal'
import { CreativePublishWorksModal } from '@/features/studio/modals/CreativePublishWorksModal'
import { ProductionBudgetGate } from '@/features/studio/modals/ProductionBudgetGate'
import { CanvasDock } from '@/features/studio/canvas/CanvasDock'
import {
  libraryAssetDataType,
  loadDockLibraryAssets,
  persistGeneratedPreviewToLibrary,
  uploadNodeUsesAsset,
  type DockLibraryAsset,
} from '@/features/studio/canvas/canvas-dock-library'
import {
  CanvasPaneContextMenu,
  type CanvasPaneContextMenuState,
} from '@/features/studio/canvas/CanvasPaneContextMenu'
import { PromptLibraryDrawer } from '@/features/studio/canvas/PromptLibraryDrawer'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { ProtoBtn } from '@/features/studio/components/ui-kit'
import { CanvasViewportControls } from '@/features/studio/canvas/CanvasViewportControls'
import { CanvasMiniMap } from '@/features/studio/canvas/CanvasMiniMap'
import { CanvasNodeProvider } from '@/features/studio/canvas/CanvasNodeContext'
import { CanvasTopBar } from '@/features/studio/canvas/CanvasTopBar'
import { CanvasStylePicker } from '@/features/studio/canvas/theme/CanvasStylePicker'
import { CanvasThemeProvider, useCanvasTheme } from '@/features/studio/canvas/theme/CanvasThemeContext'
import { StudioEdge } from '@/features/studio/canvas/edges/StudioEdge'
import { StudioNode } from '@/features/studio/canvas/nodes/StudioNode'
import { GroupNode } from '@/features/studio/canvas/nodes/GroupNode'
import { mergeGenParams, buildVideoSubmitPayload, primaryParamFieldsForNode, advancedParamFieldsForNode } from '@/features/studio/canvas/model-params'
import { normalizeAudioSubmitPayload } from '@/features/studio/canvas/audio-params'
import { muxVideoWithAudio } from '@/features/studio/canvas/media-compose'
import { trimAudioToWav } from '@/features/studio/canvas/audio-trim'
import {
  appendAudioTabTake,
  AUDIO_PRODUCT_TABS,
  ensureAudioNodeData,
  getActiveAudioTab,
  preferAudioTabTakeOnNode,
} from '@/features/studio/canvas/audio-tabs'
import {
  acceptsReferenceImages,
  outputDataTypeForNode,
  taskTypeForNode,
  type HandleDataType,
  type AudioProductTab,
  type StudioFlowEdge,
  type StudioFlowNode,
  type StudioNodeData,
  type StudioNodeType,
} from '@/features/studio/canvas/types'
import {
  buildCtxTemplate,
  collectPromptFromUpstream,
  collectUpstreamAudio,
  collectUpstreamImages,
  collectUpstreamVideo,
  createStudioNode,
  groupSelectedNodes,
  isValidStudioConnection,
  migrateNode,
  parseCanvasLaunch,
  placeBeside,
  refreshUpstreamPreviews,
  ungroupNode,
  type CanvasLaunchContext,
} from '@/features/studio/canvas/utils'
import {
  detectWorkbenchDuplicates,
  hasWorkbenchDuplicates,
  mergeWorkbenchLaunchIntoGraph,
  type WorkbenchDuplicateInfo,
} from '@/features/studio/canvas/workbench-seed'
import { listSceneReferenceAssets } from '@/features/studio/lib/scene-reference-assets'
import { useAIModels } from '@/features/studio/hooks/useAIModels'
import { useCanvasGraphSave } from '@/features/studio/hooks/useCanvasGraphSave'
import { useProductionPermissions } from '@/features/studio/hooks/useProductionPermissions'
import { pollAITask } from '@/features/studio/hooks/pollAITask'
import { productionBasePath } from '@/features/studio/lib/production-path'
import { STUDIO_BASE } from '@/features/studio/lib/studio-paths'
import { buildShotSceneScopeKey } from '@/features/studio/lib/canvas-scope'
import { mergeStudioAIBillingPayload } from '@/features/studio/lib/studio-ai-billing'
import { formatResolvedApiError, resolveApiErrorMessage, simplifyAITaskErrorMessage } from '@/features/studio/lib/resolveApiError'
import { toast } from '@/shared/lib/notify'
import { isActiveMarketplaceListingStatus } from '@/features/marketplace/components/listingStatus'

const nodeTypes = { studio: StudioNode, group: GroupNode }
const edgeTypes = { studio: StudioEdge }

/** 选中连线需高于 elevateNodesOnSelect(+1000) 的节点层，否则点击/删除按钮被节点遮挡 */
const SELECTED_EDGE_Z = 10_000

function CanvasFlowInner() {
  const location = useLocation()
  const isCreativeMode = /^\/studio\/creative\//.test(location.pathname)
  const { productionId: productionIdParam = '', workspaceId = '' } = useParams()
  const productionId = isCreativeMode ? workspaceId : productionIdParam
  const [search] = useSearchParams()
  const searchString = search.toString()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { byType, defaultModelId } = useAIModels()
  const imageModels = byType('image')
  const videoModels = byType('video')
  const audioModels = byType('audio')
  const textModels = byType('text')
  const rf = useReactFlow()
  const { nodeTheme, edgeAnim, togglePicker, pickerOpen } = useCanvasTheme()

  const launch = useMemo(
    () =>
      parseCanvasLaunch(
        new URLSearchParams(searchString),
        isCreativeMode ? creativeBasePath(productionId) : productionBasePath(productionId),
      ),
    [searchString, productionId, isCreativeMode],
  )
  const canvasScope = useMemo(
    () => ({ scopeType: launch.scopeType, scopeKey: launch.scopeKey }),
    [launch.scopeType, launch.scopeKey],
  )
  /** 稳定 key：仅在项目 / 模式 / URL 查询变化时重载画布 */
  const loadContextKey = useMemo(
    () => `${productionId}|${isCreativeMode ? 'creative' : 'production'}|${searchString}`,
    [productionId, isCreativeMode, searchString],
  )

  const [production, setProduction] = useState<Production | null>(null)
  const [creativeMeta, setCreativeMeta] = useState<CreativeWorkspace | null>(null)
  const { canDelete: canDeleteFromProduction } = useProductionPermissions(production)
  const canDeleteCanvasStructure = isCreativeMode || canDeleteFromProduction
  const [nodes, setNodes, onNodesChange] = useNodesState<StudioFlowNode>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<StudioFlowEdge>([])
  const [loading, setLoading] = useState(true)
  const [saveEnabled, setSaveEnabled] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dockExpanded, setDockExpanded] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [libraryAssets, setLibraryAssets] = useState<DockLibraryAsset[]>([])
  const [libraryLoading, setLibraryLoading] = useState(false)
  const [promptsOpen, setPromptsOpen] = useState(false)
  const [reloadConfirmOpen, setReloadConfirmOpen] = useState(false)
  const [overwriteConfirmOpen, setOverwriteConfirmOpen] = useState(false)
  const [conflictBusy, setConflictBusy] = useState(false)
  const [listingOpen, setListingOpen] = useState(false)
  const [publishModalOpen, setPublishModalOpen] = useState(false)
  const [publishFocusNodeId, setPublishFocusNodeId] = useState<string | null>(null)
  const [creativeListing, setCreativeListing] = useState<StudioCreativeListing | null>(null)
  const [publishResult, setPublishResult] = useState<string | null>(null)
  const [reviewSubmitting, setReviewSubmitting] = useState(false)
  const [workbenchDupDialogOpen, setWorkbenchDupDialogOpen] = useState(false)
  const [workbenchDupInfo, setWorkbenchDupInfo] = useState<WorkbenchDuplicateInfo | null>(null)
  const workbenchMergeRef = useRef<{
    nodes: StudioFlowNode[]
    edges: StudioFlowEdge[]
    shots: Shot[]
    sceneHeading?: string
    sceneAssets: Awaited<ReturnType<typeof listSceneReferenceAssets>>
    legacyStoryboardUrl?: string
  } | null>(null)
  const [paneContextMenu, setPaneContextMenu] = useState<CanvasPaneContextMenuState | null>(null)
  const [boxSelecting, setBoxSelecting] = useState(false)
  const [graphRevision, setGraphRevision] = useState(0)
  const hydrated = useRef(false)

  const getGraphSnapshot = useCallback(
    () => ({
      nodes: rf.getNodes() as StudioFlowNode[],
      edges: rf.getEdges() as StudioFlowEdge[],
      viewport: rf.getViewport(),
    }),
    [rf],
  )

  const handleSaveError = useCallback((msg: string) => {
    setError(msg)
  }, [])

  const handleSaveSuccess = useCallback(() => {
    setError((prev) => (prev?.includes('saveFailed') || prev?.includes('conflict') ? null : prev))
  }, [])

  const {
    status: saveStatus,
    markDirty,
    flushSave,
    forceSave,
    resetClean,
    setRevision,
  } = useCanvasGraphSave({
    enabled: saveEnabled,
    graphMode: isCreativeMode ? 'creative' : 'production',
    productionId,
    canvasScope,
    getSnapshot: getGraphSnapshot,
    stripNode: stripCallbacks,
    onSaveError: handleSaveError,
    onSaveSuccess: handleSaveSuccess,
  })

  const scheduleSave = useCallback(() => {
    if (!hydrated.current) return
    markDirty()
  }, [markDirty])

  const reloadLibrary = useCallback(async () => {
    if (!productionId) return
    setLibraryLoading(true)
    try {
      setLibraryAssets(await loadDockLibraryAssets({ isCreative: isCreativeMode, productionId }))
    } catch {
      setLibraryAssets([])
    } finally {
      setLibraryLoading(false)
    }
  }, [isCreativeMode, productionId])

  useEffect(() => {
    void reloadLibrary()
  }, [reloadLibrary])

  const refreshCreativeListing = useCallback(async () => {
    if (!isCreativeMode || !productionId) {
      setCreativeListing(null)
      return
    }
    try {
      const res = await listCreativeListings(productionId)
      const hit =
        res.items.find(
          (item) => isActiveMarketplaceListingStatus(item.status) && item.graph_revision === graphRevision,
        ) ?? null
      setCreativeListing(hit)
    } catch {
      setCreativeListing(null)
    }
  }, [graphRevision, isCreativeMode, productionId])

  useEffect(() => {
    if (!isCreativeMode || !hydrated.current) return
    void refreshCreativeListing()
  }, [graphRevision, isCreativeMode, refreshCreativeListing])

  const applyServerGraph = useCallback(
    (graph: StudioCanvasGraph, options?: { seedTemplate?: boolean }) => {
      setRevision(graph.revision)
      setGraphRevision(graph.revision ?? 0)
      let initialNodes = ((graph.nodes ?? []) as StudioFlowNode[]).map((n) => migrateNode(n))
      let initialEdges = ((graph.edges ?? []) as StudioFlowEdge[]).map((e) => ({
        ...e,
        type: 'studio' as const,
        data: { animated: true, ...e.data },
      }))
      const seedTemplate = options?.seedTemplate ?? true
      if (seedTemplate && initialNodes.length === 0 && launch.ctx !== 'free' && launch.ctx !== 'shot-frame') {
        const tpl = buildCtxTemplate(launch)
        initialNodes = tpl.nodes
        initialEdges = tpl.edges.map((e) => ({
          ...e,
          type: 'studio' as const,
          data: { animated: true, ...e.data },
        }))
      }
      initialNodes = initialNodes.map((n) => ensureNodeModelDefaults(n, defaultModelId, audioModels))
      initialNodes = refreshUpstreamPreviews(initialNodes, initialEdges)
      setNodes(initialNodes)
      setEdges(initialEdges)
      const vp = graph.viewport
      if (vp) {
        requestAnimationFrame(() => rf.setViewport(vp, { duration: 0 }))
      }
      resetClean()
      return { nodes: initialNodes, edges: initialEdges }
    },
    [audioModels, defaultModelId, launch, resetClean, rf, setEdges, setNodes, setRevision],
  )

  const applyWorkbenchMerge = useCallback(
    (replaceDuplicates: boolean) => {
      const pending = workbenchMergeRef.current
      if (!pending || launch.ctx !== 'shot-frame') return
      const isLegacyWorkbenchRef = (n: StudioFlowNode) =>
        n.data.nodeType === 'ref_scene' &&
        n.data.refScene?.seededFromWorkbench &&
        n.data.refScene?.sceneScopeKey === launch.scopeKey

      const { nodes: addNodes, updatedNodes } = mergeWorkbenchLaunchIntoGraph({
        launch,
        shots: pending.shots,
        sceneHeading: pending.sceneHeading,
        sceneAssets: pending.sceneAssets,
        legacyStoryboardUrl: pending.legacyStoryboardUrl,
        existingNodes: pending.nodes,
        options: { replaceDuplicates },
      })
      const updatedById = new Map(updatedNodes.map((n) => [n.id, n]))
      setNodes((prev) => {
        const withoutLegacy = prev.filter((n) => !isLegacyWorkbenchRef(n))
        const merged = withoutLegacy.map((n) => updatedById.get(n.id) ?? n)
        const next = [...merged, ...addNodes]
        return refreshUpstreamPreviews(next, pending.edges)
      })
      setEdges((prev) => {
        const removedIds = new Set(
          pending.nodes.filter(isLegacyWorkbenchRef).map((n) => n.id),
        )
        if (removedIds.size === 0) return prev
        return prev.filter((e) => !removedIds.has(e.source) && !removedIds.has(e.target))
      })
      const removedRefCount = pending.nodes.filter(isLegacyWorkbenchRef).length
      if (addNodes.length > 0 || updatedNodes.length > 0 || removedRefCount > 0) {
        scheduleSave()
      }
      workbenchMergeRef.current = null
      setWorkbenchDupInfo(null)
      setWorkbenchDupDialogOpen(false)
    },
    [launch, scheduleSave, setEdges, setNodes],
  )

  const applyWorkbenchMergeRef = useRef(applyWorkbenchMerge)
  applyWorkbenchMergeRef.current = applyWorkbenchMerge

  const applyServerGraphRef = useRef(applyServerGraph)
  applyServerGraphRef.current = applyServerGraph

  const reloadFromServer = useCallback(async () => {
    setConflictBusy(true)
    setError(null)
    try {
      const graph = isCreativeMode
        ? await getCreativeGraph(productionId)
        : await getCanvasGraph(productionId, canvasScope)
      applyServerGraph(graph)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'canvas.errors.loadFailed'))
    } finally {
      setConflictBusy(false)
      setReloadConfirmOpen(false)
    }
  }, [applyServerGraph, canvasScope, isCreativeMode, productionId])

  const handleConfirmOverwrite = useCallback(async () => {
    setConflictBusy(true)
    setError(null)
    try {
      const ok = await forceSave()
      if (!ok) return
      setOverwriteConfirmOpen(false)
    } finally {
      setConflictBusy(false)
    }
  }, [forceSave])

  const patchNodeDraft = useCallback(
    (
      nodeId: string,
      patch: Partial<StudioNodeData> | ((data: StudioNodeData) => Partial<StudioNodeData>),
    ) => {
      setNodes((prev) =>
        prev.map((n) => {
          if (n.id !== nodeId) return n
          const delta = typeof patch === 'function' ? patch(n.data) : patch
          return { ...n, data: { ...n.data, ...delta } }
        }),
      )
      scheduleSave()
    },
    [setNodes, scheduleSave],
  )

  const patchNodeData = useCallback(
    (
      nodeId: string,
      patch: Partial<StudioNodeData> | ((data: StudioNodeData) => Partial<StudioNodeData>),
      options?: { persist?: boolean },
    ) => {
      setNodes((prev) => {
        const next = prev.map((n) => {
          if (n.id !== nodeId) return n
          const delta = typeof patch === 'function' ? patch(n.data) : patch
          return { ...n, data: { ...n.data, ...delta } }
        })
        return refreshUpstreamPreviews(next, edges)
      })
      if (options?.persist !== false) scheduleSave()
    },
    [edges, setNodes, scheduleSave],
  )

  const handleRun = useCallback(
    async (
      nodeId: string,
      opts?: {
        textInstruction?: string
        promptPositive?: string
        promptNegative?: string
        genParams?: Record<string, string | number>
      },
    ) => {
      const node = rf.getNode(nodeId) as StudioFlowNode | undefined
      if (!node) return
      const nodeType = node.data.nodeType
      const nodeData =
        nodeType === 'gen_audio' ? ensureAudioNodeData(node.data, audioModels) : node.data

      if (nodeType === 'text') {
        const modelId = node.data.gen?.modelId || defaultModelId('text')
        if (!modelId) {
          setError(t('canvas.errors.noModel'))
          return
        }
        const instruction =
          opts?.textInstruction?.trim() ??
          node.data.text?.instruction?.trim() ??
          ''
        const upstreamPrompt = collectPromptFromUpstream(
          nodeId,
          rf.getNodes() as StudioFlowNode[],
          rf.getEdges() as StudioFlowEdge[],
        )
        const prompt = [instruction, upstreamPrompt.positive.trim()].filter(Boolean).join('\n\n').trim()
        if (!prompt) {
          setError(t('canvas.errors.noTextInstruction'))
          return
        }

        patchNodeData(nodeId, { output: { status: 'running', dataType: 'text' } }, { persist: false })
        setError(null)
        try {
          const task = await aiApi.submitText({
            model_id: modelId,
            prompt,
            payload: mergeStudioAIBillingPayload(
              isCreativeMode ? undefined : productionId,
              undefined,
              canvasScope,
            ),
          })
          const final = await pollAITask(task.id, () => {})
          const generated = extractTextOutput(final.output).trim()
          if (final.status !== 'succeeded' || !generated) {
            throw new Error(final.error_message || t('canvas.errors.generateFailed'))
          }
          patchNodeData(nodeId, {
            text: {
              content: generated,
              instruction: node.data.text?.instruction ?? '',
            },
            output: {
              status: 'succeeded',
              dataType: 'text',
            },
          })
        } catch (e) {
          const resolved = resolveApiErrorMessage(e, 'canvas.errors.generateFailed')
          const errMsg = formatResolvedApiError(t, resolved)
          patchNodeData(nodeId, {
            output: {
              status: 'failed',
              error: errMsg,
              dataType: 'text',
            },
          })
          setError(errMsg)
        }
        return
      }

      const taskType = taskTypeForNode(nodeType)
      if (!taskType) return
      const modelId = nodeData.gen?.modelId || defaultModelId(taskType)
      if (!modelId) {
        setError(t('canvas.errors.noModel'))
        return
      }

      const upstreamPrompt = collectPromptFromUpstream(
        nodeId,
        rf.getNodes() as StudioFlowNode[],
        rf.getEdges() as StudioFlowEdge[],
      )
      let positive = (
        opts?.promptPositive?.trim() ||
        nodeData.prompt?.positive?.trim() ||
        upstreamPrompt.positive.trim() ||
        launch.label
      ).trim()
      // 音乐「描述生曲」：无外置 prompt 时回退 style 文案
      if (
        !positive &&
        nodeType === 'gen_audio' &&
        String(nodeData.gen?.params?.music_mode ?? 'describe') !== 'lyrics'
      ) {
        positive = String(nodeData.gen?.params?.style ?? '').trim()
      }
      // 音乐「歌词生曲」：允许仅用 lyrics，无外置 prompt
      if (
        !positive &&
        nodeType === 'gen_audio' &&
        String(nodeData.gen?.params?.music_mode ?? '') === 'lyrics'
      ) {
        positive = String(nodeData.gen?.params?.lyrics ?? '').trim()
      }
      if (!positive.trim()) {
        setError(t('canvas.errors.noPrompt'))
        return
      }
      const negative = (
        opts?.promptNegative?.trim() ||
        nodeData.prompt?.negative?.trim() ||
        upstreamPrompt.negative ||
        ''
      ).trim()

      patchNodeData(nodeId, { output: { status: 'running', dataType: outputDataTypeForNode(nodeType) } }, { persist: false })
      setError(null)
      try {
        const payload: Record<string, unknown> = mergeStudioAIBillingPayload(
          isCreativeMode ? undefined : productionId,
          nodeType === 'gen_video' ? undefined : { ...(nodeData.gen?.params ?? {}) },
          canvasScope,
        )
        if (negative) payload.negative_prompt = negative

        if (acceptsReferenceImages(nodeType)) {
          const refImages = collectUpstreamImages(
            nodeId,
            rf.getNodes() as StudioFlowNode[],
            rf.getEdges() as StudioFlowEdge[],
          )
          if (refImages.length > 0) {
            payload.reference_image = refImages[0]
            payload.reference_images = refImages
          }
        }
        if (nodeType === 'gen_audio') {
          const refAudio = collectUpstreamAudio(
            nodeId,
            rf.getNodes() as StudioFlowNode[],
            rf.getEdges() as StudioFlowEdge[],
          )
          if (refAudio.length > 0) {
            payload.reference_audio = refAudio[0]
            payload.reference_audios = refAudio.slice(0, 3)
          }
          const audioNorm = normalizeAudioSubmitPayload(modelId, payload) as Record<string, unknown>
          for (const key of Object.keys(payload)) {
            if (!(key in audioNorm)) delete payload[key]
          }
          Object.assign(payload, audioNorm)
        }
        if (nodeType === 'gen_video') {
          const videoFields = [
            ...primaryParamFieldsForNode('gen_video'),
            ...advancedParamFieldsForNode('gen_video'),
          ]
          const videoPayload = buildVideoSubmitPayload(
            opts?.genParams ?? (nodeData.gen?.params as Record<string, string | number> | undefined),
            videoFields,
            payload,
          )
          for (const key of Object.keys(payload)) {
            delete payload[key]
          }
          Object.assign(payload, videoPayload)
        }

        const submit =
          taskType === 'video'
            ? () => aiApi.submitVideo({ model_id: modelId, prompt: positive, payload })
            : taskType === 'audio'
              ? () => aiApi.submitAudio({ model_id: modelId, prompt: positive, payload })
              : () => aiApi.submitImage({ model_id: modelId, prompt: positive, payload })

        const task = await submit()
        const outDataType = outputDataTypeForNode(nodeType)
        const final = await pollAITask(
          task.id,
          (updated) => {
            if (updated.status === 'pending' || updated.status === 'running') {
              patchNodeData(
                nodeId,
                { output: { status: 'running', dataType: outDataType } },
                { persist: false },
              )
            }
          },
          { taskType, modelId, promptLength: positive.length },
        )
        const previewUrl =
          outDataType === 'video'
            ? extractVideoUrl(final.output)
            : outDataType === 'audio'
              ? extractAudioUrl(final.output)
              : extractImageUrl(final.output)
        if (final.status === 'succeeded' && !previewUrl && outDataType === 'image') {
          throw new Error(t('canvas.errors.noImageOutput'))
        }
        const failedErrMsg =
          final.status === 'failed'
            ? formatResolvedApiError(
                t,
                simplifyAITaskErrorMessage(final.error_message, 'canvas.errors.generateFailed'),
              )
            : undefined
        if (failedErrMsg) setError(failedErrMsg)
        const allModels = [...imageModels, ...videoModels, ...audioModels]
        const modelName = allModels.find((m) => m.id === modelId)?.name
        const takes = [...(nodeData.takes ?? [])]
        const version = (takes[takes.length - 1]?.version ?? 0) + 1
        const newTake = {
          version,
          status: final.status,
          cost: final.cost,
          modelId,
          modelName,
          previewUrl,
          taskId: task.id,
          createdAt: new Date().toISOString(),
        }
        if (nodeType === 'gen_audio') {
          const tab = getActiveAudioTab(nodeData)
          patchNodeData(nodeId, {
            output: {
              status: final.status === 'succeeded' ? 'succeeded' : 'failed',
              previewUrl,
              dataType: outDataType,
              error: failedErrMsg,
            },
            ...appendAudioTabTake(nodeData, tab, newTake, audioModels),
          })
        } else {
          takes.push(newTake)
          patchNodeData(nodeId, {
            output: {
              status: final.status === 'succeeded' ? 'succeeded' : 'failed',
              previewUrl,
              dataType: outDataType,
              error: failedErrMsg,
            },
            takes,
            preferredTakeVersion: version,
          })
        }
        if (final.status === 'succeeded' && previewUrl) {
          const filename = `${nodeData.label || nodeType}-v${version}`
          const productionKind =
            outDataType === 'video' ? 'take_video' : outDataType === 'audio' ? 'misc' : 'take_image'
          void persistGeneratedPreviewToLibrary({
            isCreative: isCreativeMode,
            productionId,
            url: previewUrl,
            filename,
            productionKind,
          })
            .then(() => {
              void reloadLibrary()
            })
            .catch(() => {
              /* 生成成功不因入库失败回滚 */
            })
        }
      } catch (e) {
        const resolved = resolveApiErrorMessage(e, 'canvas.errors.generateFailed', { taskType })
        const errMsg = formatResolvedApiError(t, resolved)
        patchNodeData(nodeId, {
          output: {
            status: 'failed',
            error: errMsg,
            dataType: outputDataTypeForNode(nodeType),
          },
        })
        setError(errMsg)
      }
    },
    [
      audioModels,
      canvasScope,
      defaultModelId,
      imageModels,
      isCreativeMode,
      launch.label,
      patchNodeData,
      productionId,
      reloadLibrary,
      rf,
      t,
      textModels,
      videoModels,
    ],
  )

  const handlePreferTake = useCallback(
    (nodeId: string, version: number, audioTab?: AudioProductTab) => {
      const node = rf.getNode(nodeId) as StudioFlowNode | undefined
      if (!node) return
      setNodes((nds) => nds.map((n) => ({ ...n, selected: n.id === nodeId })))
      if (node.data.nodeType === 'gen_audio') {
        const data = ensureAudioNodeData(node.data, audioModels)
        const tab = audioTab ?? getActiveAudioTab(data)
        patchNodeData(nodeId, preferAudioTabTakeOnNode(data, tab, version, audioModels))
        return
      }
      const take = node.data.takes?.find((x) => x.version === version)
      patchNodeData(nodeId, {
        preferredTakeVersion: version,
        output: {
          status: take?.status === 'succeeded' ? 'succeeded' : 'failed',
          previewUrl: take?.previewUrl,
          dataType: outputDataTypeForNode(node.data.nodeType),
        },
      })
    },
    [audioModels, patchNodeData, rf, setNodes],
  )

  const handleSetWorkspaceCover = useCallback(
    async (previewUrl: string) => {
      if (!isCreativeMode || !productionId) return
      const coverUrl = previewUrl.trim()
      if (!coverUrl) return
      try {
        const updated = await patchCreativeWorkspace(productionId, { cover_url: coverUrl })
        setCreativeMeta(updated)
        toast.success(t('canvas.historyCoverSet'))
      } catch (e) {
        toast.error(
          formatResolvedApiError(t, resolveApiErrorMessage(e, 'canvas.historyCoverSetFailed')),
        )
      }
    },
    [isCreativeMode, productionId, t],
  )

  const handleFocusUpstream = useCallback(
    (nodeId: string) => {
      const n = rf.getNode(nodeId)
      if (!n) return
      rf.setCenter(n.position.x + 160, n.position.y + 100, { zoom: rf.getZoom(), duration: 300 })
    },
    [rf],
  )

  const handleExportCompose = useCallback(
    async (nodeId: string) => {
      const node = rf.getNode(nodeId) as StudioFlowNode | undefined
      if (!node || node.data.nodeType !== 'export') return
      const nodesSnap = rf.getNodes() as StudioFlowNode[]
      const edgesSnap = rf.getEdges() as StudioFlowEdge[]
      const videos = collectUpstreamVideo(nodeId, nodesSnap, edgesSnap)
      const audios = collectUpstreamAudio(nodeId, nodesSnap, edgesSnap)
      if (!videos[0]) {
        setError(t('canvas.errors.exportNeedVideo'))
        return
      }
      patchNodeData(nodeId, { export: { ...node.data.export, status: 'running', error: undefined } }, { persist: false })
      setError(null)
      try {
        let outputUrl = videos[0]
        if (audios[0]) {
          const blob = await muxVideoWithAudio(videos[0], audios[0])
          const filename = `compose-${Date.now()}.webm`
          const file = new File([blob], filename, { type: blob.type || 'video/webm' })
          outputUrl = URL.createObjectURL(blob)
          try {
            const saved = await persistCanvasFile({
              isCreative: isCreativeMode,
              productionId,
              file,
              productionKind: 'take_video',
            })
            outputUrl = saved.url
          } catch {
            /* 合成预览仍可用本地 blob；落库失败不阻断本次导出 */
          }
        }
        const title = node.data.export?.title || node.data.label
        patchNodeData(nodeId, {
          export: { status: 'succeeded', outputUrl, title },
          output: { status: 'succeeded', previewUrl: outputUrl, dataType: 'video' },
        })
        void reloadLibrary()
      } catch (e) {
        const msg = resolveApiErrorMessage(e, 'canvas.errors.exportComposeFailed')
        patchNodeData(nodeId, {
          export: { ...node.data.export, status: 'failed', error: msg },
        })
        setError(msg)
      }
    },
    [isCreativeMode, patchNodeData, productionId, reloadLibrary, rf, t],
  )

  const handleTrimAudio = useCallback(
    async (nodeId: string, startSec: number, endSec: number) => {
      const node = rf.getNode(nodeId) as StudioFlowNode | undefined
      if (!node) return
      const srcUrl =
        node.data.output?.previewUrl ||
        (node.data.upload?.mime?.startsWith('audio/') ? node.data.upload.url : undefined)
      if (!srcUrl) {
        setError(t('canvas.errors.audioTrimNoSource'))
        return
      }
      setError(null)
      try {
        const wav = await trimAudioToWav(srcUrl, startSec, endSec)
        const filename = `${(node.data.label || 'audio').replace(/\s+/g, '_')}_trim.wav`
        const file = new File([wav], filename, { type: 'audio/wav' })
        const saved = await persistCanvasFile({
          isCreative: isCreativeMode,
          productionId,
          file,
          productionKind: 'misc',
        })
        const url = saved.url
        if (!url) {
          setError(t('canvas.errors.uploadNoUrl'))
          return
        }
        const pos = placeBeside(node, 280, 40)
        const uploadNode = ensureNodeModelDefaults(
          createStudioNode('upload', pos, {
            label: t('canvas.audioTrim.nodeName', { name: node.data.label || 'audio' }),
            upload: { url, mime: 'audio/wav', filename, assetPublicId: saved.publicId },
            output: { status: 'succeeded', previewUrl: url, dataType: 'audio' },
          }),
          defaultModelId,
          audioModels,
        )
        setNodes((nds) => [...nds, uploadNode])
        scheduleSave()
        void reloadLibrary()
      } catch (e) {
        setError(resolveApiErrorMessage(e, 'canvas.errors.audioTrimFailed'))
      }
    },
    [audioModels, defaultModelId, isCreativeMode, productionId, reloadLibrary, rf, scheduleSave, setNodes, t],
  )

  const handleUploadFile = useCallback(
    (nodeId: string, file: File | null, kind?: 'image' | 'video' | 'audio') => {
      if (!file) {
        const input = document.createElement('input')
        input.type = 'file'
        const accept =
          kind === 'image' ? 'image/*' : kind === 'video' ? 'video/*' : kind === 'audio' ? 'audio/*' : 'image/*,video/*,audio/*'
        input.accept = accept
        input.onchange = () => {
          const f = input.files?.[0]
          if (f) void handleUploadFile(nodeId, f, kind)
        }
        input.click()
        return
      }
      const node = rf.getNode(nodeId) as StudioFlowNode | undefined
      if (!node) return
      const nodeType = node.data.nodeType
      void (async () => {
        try {
          setError(null)
          const saved = await persistCanvasFile({
            isCreative: isCreativeMode,
            productionId,
            file,
            productionKind:
              nodeType === 'export'
                ? 'take_video'
                : nodeType === 'upload'
                  ? 'misc'
                  : nodeType === 'ref_character'
                    ? 'character_ref'
                    : 'location_ref',
          })
          const url = saved.url
          if (!url) {
            setError(t('canvas.errors.uploadNoUrl'))
            return
          }
          if (nodeType === 'export') {
            const title = node.data.export?.title || node.data.label || file.name
            patchNodeData(nodeId, {
              label: node.data.label || file.name,
              export: { status: 'succeeded', outputUrl: url, title, error: undefined },
              output: { status: 'succeeded', previewUrl: url, dataType: 'video' },
            })
          } else if (nodeType === 'upload') {
            patchNodeData(nodeId, {
              upload: { url, mime: file.type, filename: file.name, assetPublicId: saved.publicId },
              output: { status: 'succeeded', previewUrl: url, dataType: mimeToDataType(file.type) },
            })
          } else if (nodeType === 'ref_character') {
            const urls = [...(node.data.refCharacter?.urls ?? []), url]
            patchNodeData(nodeId, { refCharacter: { ...node.data.refCharacter, urls } as typeof node.data.refCharacter })
          } else if (nodeType === 'ref_scene') {
            const urls = [...(node.data.refScene?.urls ?? []), url]
            patchNodeData(nodeId, { refScene: { ...node.data.refScene, urls } as typeof node.data.refScene })
          }
          void reloadLibrary()
        } catch (e) {
          setError(resolveApiErrorMessage(e, 'canvas.errors.uploadFailed'))
        }
      })()
    },
    [isCreativeMode, patchNodeData, productionId, reloadLibrary, rf, t],
  )

  useEffect(() => {
    hydrated.current = false
    setSaveEnabled(false)
    let cancelled = false
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        if (isCreativeMode) {
          const [meta, graph, listingRes] = await Promise.all([
            getCreativeWorkspace(productionId),
            getCreativeGraph(productionId),
            listCreativeListings(productionId).catch(() => ({ items: [] as StudioCreativeListing[] })),
          ])
          if (cancelled) return
          setCreativeMeta(meta)
          setProduction(null)
          applyServerGraphRef.current(graph, { seedTemplate: false })
          const rev = graph.revision ?? 0
          const hit =
            listingRes.items.find(
              (item) => isActiveMarketplaceListingStatus(item.status) && item.graph_revision === rev,
            ) ?? null
          setCreativeListing(hit)
          hydrated.current = true
          setSaveEnabled(true)
        } else {
          const [prod, graph] = await Promise.all([
            getProduction(productionId),
            getCanvasGraph(productionId, canvasScope),
          ])
          if (cancelled) return
          setProduction(prod)
          setCreativeMeta(null)
          const { nodes: loadedNodes, edges: loadedEdges } = applyServerGraphRef.current(graph, {
            seedTemplate: launch.ctx !== 'shot-frame',
          })
          hydrated.current = true
          setSaveEnabled(true)

          if (
            launch.ctx === 'shot-frame' &&
            launch.shotIds?.length &&
            launch.scriptId != null &&
            launch.episodeNo != null &&
            launch.sceneNo != null
          ) {
            try {
              const [shotRes, sceneAssets, scriptRes] = await Promise.all([
                listShots(productionId, {
                  script_id: launch.scriptId,
                  episode_no: launch.episodeNo,
                  limit: 500,
                }),
                listSceneReferenceAssets(productionId, launch.scopeKey),
                getScript(productionId, launch.scriptId, launch.episodeNo).catch(() => null),
              ])
              if (cancelled) return
              const shots = launch.shotIds
                .map((id) => shotRes.items.find((s) => s.public_id === id))
                .filter((s): s is Shot => s != null && s.scene_no === launch.sceneNo)
              const sceneHeading = scriptRes?.scenes?.find((sc) => sc.scene_no === launch.sceneNo)?.heading
              const legacyStoryboardUrl = scriptRes?.scenes?.find(
                (sc) => sc.scene_no === launch.sceneNo,
              )?.storyboard_url

              const dupes = detectWorkbenchDuplicates(loadedNodes, shots)
              workbenchMergeRef.current = {
                nodes: loadedNodes,
                edges: loadedEdges,
                shots,
                sceneHeading,
                sceneAssets,
                legacyStoryboardUrl,
              }

              if (hasWorkbenchDuplicates(dupes)) {
                setWorkbenchDupInfo(dupes)
                setWorkbenchDupDialogOpen(true)
              } else {
                applyWorkbenchMergeRef.current(false)
              }
            } catch {
              /* workbench merge optional */
            }
          }
        }
      } catch (e) {
        if (!cancelled) setError(resolveApiErrorMessage(e, 'canvas.errors.loadFailed'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadContextKey 已聚合全部会触发重载的 primitive
  }, [loadContextKey])

  useEffect(() => {
    if (!hydrated.current || loading) return
    if (!defaultModelId('image') && !defaultModelId('audio')) return
    setNodes((prev) => {
      const next = prev.map((n) => ensureNodeModelDefaults(n, defaultModelId, audioModels))
      const changed = next.some((n, i) => nodeDataNeedsSync(n.data, prev[i]?.data))
      return changed ? next : prev
    })
    // defaultModelId 每轮渲染是新函数，故意不放入 deps，仅在 loading / 模型列表就绪时补齐
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, audioModels.length, setNodes])

  const isDirtyNodeChange = useCallback((changes: NodeChange<StudioFlowNode>[]) => {
    return changes.some((c) => c.type !== 'select')
  }, [])

  const isDirtyEdgeChange = useCallback((changes: EdgeChange<StudioFlowEdge>[]) => {
    return changes.some((c) => c.type !== 'select')
  }, [])

  const handleNodesChange = useCallback(
    (changes: NodeChange<StudioFlowNode>[]) => {
      onNodesChange(changes)
      if (isDirtyNodeChange(changes)) scheduleSave()
    },
    [onNodesChange, isDirtyNodeChange, scheduleSave],
  )

  const handleEdgesChange = useCallback(
    (changes: EdgeChange<StudioFlowEdge>[]) => {
      onEdgesChange(changes)
      if (changes.some((c) => c.type === 'select')) {
        setEdges((eds) =>
          eds.map((e) => ({
            ...e,
            zIndex: e.selected ? SELECTED_EDGE_Z : 0,
          })),
        )
      }
      if (isDirtyEdgeChange(changes)) scheduleSave()
    },
    [onEdgesChange, isDirtyEdgeChange, scheduleSave, setEdges],
  )

  const handleEdgeClick = useCallback(
    (_event: ReactMouseEvent, edge: StudioFlowEdge) => {
      setEdges((eds) =>
        eds.map((e) => ({
          ...e,
          selected: e.id === edge.id,
          zIndex: e.id === edge.id ? SELECTED_EDGE_Z : 0,
        })),
      )
      setNodes((nds) => nds.map((n) => ({ ...n, selected: false })))
    },
    [setEdges, setNodes],
  )

  const onConnect = useCallback(
    (connection: Connection) => {
      const nodesSnap = rf.getNodes() as StudioFlowNode[]
      const edgesSnap = rf.getEdges() as StudioFlowEdge[]
      if (!isValidStudioConnection(connection, edgesSnap, nodesSnap)) return

      setEdges((eds) => {
        const next = addEdge({ ...connection, type: 'studio', data: { animated: true } }, eds) as StudioFlowEdge[]
        setNodes((nds) => refreshUpstreamPreviews(nds as StudioFlowNode[], next))
        return next
      })
      scheduleSave()
    },
    [rf, setEdges, setNodes, scheduleSave],
  )

  const validateConnection = useCallback<IsValidConnection<StudioFlowEdge>>(
    (connection) =>
      isValidStudioConnection(
        connection as Connection,
        rf.getEdges() as StudioFlowEdge[],
        rf.getNodes() as StudioFlowNode[],
      ),
    [rf],
  )

  const removeEdgesById = useCallback(
    (edgeIds: string[]) => {
      if (edgeIds.length === 0) return
      const drop = new Set(edgeIds)
      setEdges((eds) => {
        const next = eds.filter((e) => !drop.has(e.id))
        setNodes((nds) => refreshUpstreamPreviews(nds as StudioFlowNode[], next))
        return next
      })
      scheduleSave()
    },
    [scheduleSave, setEdges, setNodes],
  )

  const addNode = useCallback(
    (nodeType: StudioNodeType, opts?: { flowPosition?: { x: number; y: number } }) => {
      const selected = rf.getNodes().find((n) => n.selected) as StudioFlowNode | undefined
      const center = rf.screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      })
      const pos =
        opts?.flowPosition ?? (selected ? placeBeside(selected) : center)
      const node = ensureNodeModelDefaults(
        createStudioNode(nodeType, pos, {
          label: t(`canvas.node.${nodeType}`, { defaultValue: nodeType }),
        }),
        defaultModelId,
        audioModels,
      )
      setNodes((nds) => [...nds, node])
      setDockExpanded(false)
      scheduleSave()
    },
    [audioModels, defaultModelId, rf, setNodes, scheduleSave, t],
  )

  const openContextMenuAt = useCallback(
    (clientX: number, clientY: number) => {
      const flowPosition = rf.screenToFlowPosition({ x: clientX, y: clientY })
      setPaneContextMenu({
        x: clientX,
        y: clientY,
        flowX: flowPosition.x,
        flowY: flowPosition.y,
      })
    },
    [rf],
  )

  const handlePaneContextMenu = useCallback(
    (event: ReactMouseEvent | MouseEvent) => {
      event.preventDefault()
      openContextMenuAt(event.clientX, event.clientY)
    },
    [openContextMenuAt],
  )

  const handleNodeContextMenu = useCallback(
    (event: ReactMouseEvent, _node: StudioFlowNode) => {
      event.preventDefault()
      event.stopPropagation()
      openContextMenuAt(event.clientX, event.clientY)
    },
    [openContextMenuAt],
  )

  const handlePaneClick = useCallback(() => {
    setPaneContextMenu(null)
  }, [])

  const handleSelectionStart = useCallback(() => {
    setPaneContextMenu(null)
    setBoxSelecting(true)
  }, [])

  const handleSelectionEnd = useCallback(() => {
    setBoxSelecting(false)
  }, [])

  const handleContextMenuAdd = useCallback(
    (nodeType: StudioNodeType, flowPosition: { x: number; y: number }) => {
      addNode(nodeType, { flowPosition })
    },
    [addNode],
  )

  const handleGroupSelection = useCallback(() => {
    setNodes((prev) => groupSelectedNodes(prev) ?? prev)
    scheduleSave()
  }, [scheduleSave, setNodes])

  const handleUngroupSelection = useCallback(() => {
    setNodes((prev) => {
      const group = prev.find((n) => n.selected && (n.type === 'group' || n.data.nodeType === 'group'))
      if (!group) return prev
      return ungroupNode(prev, group.id) ?? prev
    })
    scheduleSave()
  }, [scheduleSave, setNodes])

  const canGroup = useMemo(
    () => nodes.filter((n) => n.selected && n.type !== 'group' && n.data.nodeType !== 'group' && !n.parentId).length >= 2,
    [nodes],
  )

  const canUngroup = useMemo(
    () => nodes.some((n) => n.selected && (n.type === 'group' || n.data.nodeType === 'group')),
    [nodes],
  )

  const placedAssetIds = useMemo(() => {
    const ids = new Set<string>()
    for (const node of nodes) {
      for (const asset of libraryAssets) {
        if (uploadNodeUsesAsset(node, asset)) {
          ids.add(asset.publicId)
          ids.add(asset.id)
        }
      }
    }
    return ids
  }, [libraryAssets, nodes])

  const handlePlaceLibraryAsset = useCallback(
    (asset: DockLibraryAsset) => {
      const existing = (rf.getNodes() as StudioFlowNode[]).find((n) => uploadNodeUsesAsset(n, asset))
      if (existing) {
        setNodes((nds) => nds.map((n) => ({ ...n, selected: n.id === existing.id })))
        const abs = existing.position
        rf.setCenter(abs.x + 160, abs.y + 120, { zoom: rf.getZoom(), duration: 280 })
        return
      }
      const pos = rf.screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      })
      const node = ensureNodeModelDefaults(
        createStudioNode('upload', pos, {
          label: asset.name,
          upload: {
            url: asset.url,
            mime: asset.mime,
            filename: asset.name,
            assetPublicId: asset.publicId,
          },
          output: { status: 'succeeded', previewUrl: asset.url, dataType: libraryAssetDataType(asset) },
        }),
        defaultModelId,
        audioModels,
      )
      setNodes((nds) => [...nds, node])
      scheduleSave()
    },
    [audioModels, defaultModelId, rf, scheduleSave, setNodes],
  )

  const handleDockToggle = useCallback(() => {
    setDockExpanded((open) => {
      if (!open) {
        setLibraryOpen(false)
        setPromptsOpen(false)
      }
      return !open
    })
  }, [])

  const handleLibraryToggle = useCallback(() => {
    setLibraryOpen((open) => {
      if (!open) {
        setDockExpanded(false)
        setPromptsOpen(false)
      }
      return !open
    })
  }, [])

  const handlePromptsToggle = useCallback(() => {
    setPromptsOpen((open) => {
      if (!open) {
        setDockExpanded(false)
        setLibraryOpen(false)
      }
      return !open
    })
  }, [])

  const handleRegisterReturn = useCallback(async () => {
    if (isCreativeMode) {
      setError(t('canvas.errors.registerCreativeUnsupported'))
      return
    }
    const registerNodes = (rf.getNodes() as StudioFlowNode[]).filter((n) => n.data.nodeType === 'register')
    const target =
      registerNodes.find((n) => n.selected) ??
      registerNodes.find((n) => n.data.output?.previewUrl || n.data.upstreamPreviews?.some((p) => p.url))
    if (!target) {
      setError(t('canvas.errors.noRegister'))
      return
    }
    const upstreamUrl =
      target.data.output?.previewUrl ??
      computeRegisterPreview(target.id, rf.getNodes() as StudioFlowNode[], rf.getEdges() as StudioFlowEdge[])
    if (!upstreamUrl) {
      setError(t('canvas.errors.noOutput'))
      return
    }
    try {
      const body = buildRegisterAssetBody(
        target,
        upstreamUrl,
        launch,
        t('canvas.untitled'),
        rf.getNodes() as StudioFlowNode[],
        rf.getEdges() as StudioFlowEdge[],
      )
      if (saveStatus === 'conflict') return
      const saved = await flushSave()
      if (!saved) return
      await registerProductionAsset(productionId, body)
      navigate(launch.returnPath)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'canvas.errors.registerFailed'))
    }
  }, [isCreativeMode, launch, navigate, productionId, rf, t, flushSave, saveStatus])

  const handleSubmitReview = useCallback(async () => {
    if (isCreativeMode || launch.ctx === 'free') return
    if (saveStatus === 'conflict') return

    setReviewSubmitting(true)
    setError(null)
    try {
      const saved = await flushSave()
      if (!saved) return

      const nodes = rf.getNodes() as StudioFlowNode[]
      const edges = rf.getEdges() as StudioFlowEdge[]
      let submittedAny = false

      for (const target of nodes.filter((n) => n.data.nodeType === 'register')) {
        const upstreamUrl =
          target.data.output?.previewUrl ?? computeRegisterPreview(target.id, nodes, edges)
        if (!upstreamUrl) continue

        const body = buildRegisterAssetBody(target, upstreamUrl, launch, t('canvas.untitled'), nodes, edges)
        const asset = await registerProductionAsset(productionId, body)
        const kind = target.data.register?.kind ?? body.kind ?? ''
        const shotPublicId = target.data.register?.shotPublicId

        if (shotPublicId && (kind === 'storyboard' || kind === 'take_video')) {
          await submitShotReview(productionId, shotPublicId)
          submittedAny = true
        } else if (asset.public_id) {
          await submitAssetReview(productionId, asset.public_id)
          submittedAny = true
        }
      }

      if (!submittedAny) {
        setError(t(launch.ctx === 'shot-frame' ? 'canvas.errors.reviewNoRegisterOutput' : 'canvas.errors.noRegister'))
        return
      }
      navigate(launch.returnPath)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'reviews.actionFailed'))
    } finally {
      setReviewSubmitting(false)
    }
  }, [flushSave, isCreativeMode, launch, navigate, productionId, rf, saveStatus, t])

  const handleBack = useCallback(async () => {
    if (saveStatus === 'conflict') return
    const saved = await flushSave()
    if (saved) navigate(launch.returnPath)
  }, [flushSave, launch.returnPath, navigate, saveStatus])

  const handleManualSave = useCallback(() => {
    if (saveStatus === 'conflict') {
      setOverwriteConfirmOpen(true)
      return
    }
    void flushSave()
  }, [flushSave, saveStatus])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!canDeleteCanvasStructure) return
        const active = document.activeElement?.tagName
        if (active === 'INPUT' || active === 'TEXTAREA') return

        const selectedEdgeIds = rf.getEdges().filter((edge) => edge.selected).map((edge) => edge.id)
        if (selectedEdgeIds.length > 0) {
          e.preventDefault()
          removeEdgesById(selectedEdgeIds)
          return
        }

        const hasSelectedNodes = rf.getNodes().some((n) => n.selected)
        if (!hasSelectedNodes) return

        e.preventDefault()
        setEdges((eds) => {
          const next = eds
          setNodes((nds) => refreshUpstreamPreviews(nds.filter((n) => !n.selected), next))
          return next
        })
        scheduleSave()
      }
      if (e.key === 'n' || e.key === 'N') setDockExpanded((v) => !v)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'g' && !e.shiftKey) {
        e.preventDefault()
        handleGroupSelection()
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'g' && e.shiftKey) {
        e.preventDefault()
        handleUngroupSelection()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    rf,
    removeEdgesById,
    setEdges,
    setNodes,
    scheduleSave,
    handleGroupSelection,
    handleUngroupSelection,
    canDeleteCanvasStructure,
  ])

  const nodeContextValue = useMemo(
    () => ({
      aspectRatio: production?.aspect_ratio ?? '9:16',
      imageModels,
      videoModels,
      audioModels,
      textModels,
      onRun: (
        nodeId: string,
        opts?: {
          textInstruction?: string
          promptPositive?: string
          promptNegative?: string
          genParams?: Record<string, string | number>
        },
      ) => void handleRun(nodeId, opts),
      onPatchDraft: patchNodeDraft,
      onPatch: patchNodeData,
      onFocusUpstream: handleFocusUpstream,
      onPreferTake: handlePreferTake,
      onUploadFile: handleUploadFile,
      onExportCompose: handleExportCompose,
      isCreative: isCreativeMode,
      onOpenPublishWorks: isCreativeMode
        ? (exportNodeId: string) => {
            setPublishFocusNodeId(exportNodeId)
            setPublishModalOpen(true)
          }
        : undefined,
      onTrimAudio: handleTrimAudio,
      onNodeGeometryChange: scheduleSave,
      onDeleteEdge: canDeleteCanvasStructure ? removeEdgesById : undefined,
    }),
    [
      production?.aspect_ratio,
      imageModels,
      videoModels,
      audioModels,
      textModels,
      handleRun,
      patchNodeDraft,
      patchNodeData,
      handleFocusUpstream,
      handlePreferTake,
      handleUploadFile,
      handleExportCompose,
      isCreativeMode,
      handleTrimAudio,
      scheduleSave,
      canDeleteCanvasStructure,
      removeEdgesById,
    ],
  )

  const canListCreative = isCreativeMode && nodes.length > 0
  const creativeListingLabel = creativeListing
    ? t('marketplace.listingStatus.' + creativeListing.status, {
        defaultValue: creativeListing.status,
      })
    : null

  const handleBeforeCreativeListing = useCallback(async (): Promise<boolean> => {
    if (saveStatus === 'conflict') return false
    const saved = await flushSave()
    if (!saved) return false
    try {
      const graph = await getCreativeGraph(productionId)
      setGraphRevision(graph.revision ?? 0)
      return (graph.nodes?.length ?? 0) > 0
    } catch {
      return nodes.length > 0
    }
  }, [flushSave, nodes.length, productionId, saveStatus])

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    )
  }

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      <svg className="absolute h-0 w-0">
        <defs>
          <linearGradient id="studio-edge-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
        </defs>
      </svg>

      <CanvasTopBar
        title={launch.label || production?.title || creativeMeta?.title || ''}
        returnPath={launch.returnPath}
        saveStatus={saveStatus}
        onBack={() => void handleBack()}
        onSave={() => void handleManualSave()}
        stylePickerOpen={pickerOpen}
        onToggleStylePicker={togglePicker}
        onRegister={() => void handleRegisterReturn()}
        hideRegister={isCreativeMode}
        showSubmitReview={!isCreativeMode && launch.ctx !== 'free'}
        onSubmitReview={() => void handleSubmitReview()}
        submitReviewBusy={reviewSubmitting}
        onList={isCreativeMode ? () => setListingOpen(true) : undefined}
        listDisabled={!canListCreative || saveStatus === 'conflict'}
        listStatusLabel={creativeListingLabel}
        onSyncWorks={undefined}
        onPublishWorks={undefined}
        canGroup={canGroup}
        canUngroup={canUngroup}
        onGroup={handleGroupSelection}
        onUngroup={handleUngroupSelection}
      />

      {saveStatus === 'conflict' ? (
        <div className="absolute left-1/2 top-14 z-30 flex w-[min(520px,calc(100vw-2rem))] -translate-x-1/2 flex-col gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-xs text-amber-100">
          <p>{t('canvas.conflictHint')}</p>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <ProtoBtn
              size="sm"
              variant="secondary"
              disabled={conflictBusy}
              onClick={() => setReloadConfirmOpen(true)}
            >
              {t('canvas.conflictReload')}
            </ProtoBtn>
            <ProtoBtn
              size="sm"
              disabled={conflictBusy}
              onClick={() => setOverwriteConfirmOpen(true)}
            >
              {t('canvas.conflictOverwrite')}
            </ProtoBtn>
          </div>
        </div>
      ) : error ? (
        <div className="absolute left-1/2 top-14 z-30 max-w-md -translate-x-1/2 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-xs text-rose-200">
          {t(error, { defaultValue: error })}
        </div>
      ) : publishResult ? (
        <div className="absolute left-1/2 top-14 z-30 max-w-lg -translate-x-1/2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-xs text-emerald-200">
          {publishResult}
        </div>
      ) : null}

      <div
        className={`studio-canvas-dots relative min-h-0 flex-1${boxSelecting ? ' studio-canvas-dots--box-selecting' : ''}`}
        data-node-theme={nodeTheme}
        data-edge-anim={edgeAnim}
      >
        <CanvasNodeProvider value={nodeContextValue}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            onEdgeClick={handleEdgeClick}
            isValidConnection={validateConnection}
            connectOnClick={false}
            onPaneContextMenu={handlePaneContextMenu}
            onNodeContextMenu={handleNodeContextMenu}
            onPaneClick={handlePaneClick}
            onSelectionStart={handleSelectionStart}
            onSelectionEnd={handleSelectionEnd}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={{ type: 'studio', data: { animated: true }, interactionWidth: 24 }}
            fitView
            minZoom={0.2}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
            noWheelClassName="nowheel"
            noPanClassName="nopan"
            noDragClassName="nodrag"
            elevateNodesOnSelect
            elevateEdgesOnSelect
            selectionOnDrag
            selectionMode={SelectionMode.Partial}
            panOnDrag={[1, 2]}
            panActivationKeyCode="Space"
            className="studio-canvas-flow !bg-transparent"
          >
            <CanvasViewportControls />
            <CanvasMiniMap />
          </ReactFlow>
        </CanvasNodeProvider>
        <CanvasPaneContextMenu
          open={paneContextMenu != null}
          anchor={paneContextMenu}
          onClose={() => setPaneContextMenu(null)}
          onAdd={handleContextMenuAdd}
          hideNodeTypes={isCreativeMode ? ['register'] : undefined}
        />
        <CanvasDock
          expanded={dockExpanded}
          onToggle={handleDockToggle}
          onAdd={addNode}
          libraryOpen={libraryOpen}
          onLibraryToggle={handleLibraryToggle}
          promptsOpen={promptsOpen}
          onPromptsToggle={handlePromptsToggle}
          libraryAssets={libraryAssets}
          libraryLoading={libraryLoading}
          placedAssetIds={placedAssetIds}
          manageHref={
            isCreativeMode
              ? `${STUDIO_BASE}/creative/assets`
              : `${STUDIO_BASE}/productions/${productionId}/assets`
          }
          onPlaceAsset={handlePlaceLibraryAsset}
          onSetWorkspaceCover={isCreativeMode ? handleSetWorkspaceCover : undefined}
          hideNodeTypes={isCreativeMode ? ['register'] : undefined}
        />
      </div>
      <PromptLibraryDrawer open={promptsOpen} onClose={() => setPromptsOpen(false)} />
      <CanvasStylePicker />

      <ConfirmDialog
        open={reloadConfirmOpen}
        title={t('canvas.conflictReloadTitle')}
        message={t('canvas.conflictReloadConfirm')}
        confirmLabel={t('canvas.conflictReload')}
        cancelLabel={t('common.cancel')}
        destructive
        busy={conflictBusy}
        onConfirm={() => void reloadFromServer()}
        onCancel={() => setReloadConfirmOpen(false)}
      />

      <ConfirmDialog
        open={overwriteConfirmOpen}
        title={t('canvas.conflictOverwriteTitle')}
        message={t('canvas.conflictOverwriteConfirm')}
        confirmLabel={t('canvas.conflictOverwrite')}
        cancelLabel={t('common.cancel')}
        destructive
        busy={conflictBusy}
        onConfirm={() => void handleConfirmOverwrite()}
        onCancel={() => setOverwriteConfirmOpen(false)}
      />

      <ConfirmDialog
        open={workbenchDupDialogOpen}
        title={t('canvas.workbenchDupTitle')}
        message={
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>{t('canvas.workbenchDupMessage')}</p>
            {workbenchDupInfo?.batchExists ? (
              <p className="text-xs text-zinc-400">{t('canvas.workbenchDupBatch')}</p>
            ) : null}
          </div>
        }
        confirmLabel={t('canvas.workbenchDupReplace')}
        cancelLabel={t('canvas.workbenchDupSkip')}
        busy={false}
        onConfirm={() => applyWorkbenchMerge(true)}
        onCancel={() => applyWorkbenchMerge(false)}
      />

      {!isCreativeMode && productionId ? <ProductionBudgetGate productionId={productionId} /> : null}

      {isCreativeMode ? (
        <>
          <CreativeWorkspaceListingModal
            open={listingOpen}
            onClose={() => setListingOpen(false)}
            workspaceId={productionId}
            defaultTitle={creativeMeta?.title || t('canvas.untitled')}
            graphRevision={graphRevision}
            nodes={nodes}
            onBeforeSubmit={handleBeforeCreativeListing}
          />
          <CreativePublishWorksModal
            open={publishModalOpen}
            onClose={() => {
              setPublishModalOpen(false)
              setPublishFocusNodeId(null)
            }}
            workspaceId={productionId}
            nodes={nodes}
            focusNodeId={publishFocusNodeId}
            onBeforeSubmit={handleBeforeCreativeListing}
            onSuccess={(message) => {
              setPublishResult(message)
              setError(null)
            }}
          />
        </>
      ) : null}
    </div>
  )
}

function stripCallbacks(node: StudioFlowNode): StudioFlowNode {
  return { ...node, data: { ...node.data } }
}

function ensureNodeModelDefaults(
  node: StudioFlowNode,
  defaultModelId: (type: 'image' | 'video' | 'audio' | 'text') => string,
  audioModels: import('@/api/types').AIModel[] = [],
): StudioFlowNode {
  if (node.data.nodeType === 'gen_audio') {
    const data = ensureAudioNodeData(node.data, audioModels)
    if (data === node.data) return node
    return { ...node, data }
  }
  if (node.data.nodeType === 'text') {
    return {
      ...node,
      data: {
        ...node.data,
        gen: {
          modelId: node.data.gen?.modelId || defaultModelId('text'),
          params: node.data.gen?.params ?? {},
        },
      },
    }
  }
  const taskType = taskTypeForNode(node.data.nodeType)
  if (!taskType) return node
  return {
    ...node,
    data: {
      ...node.data,
      gen: {
        modelId: node.data.gen?.modelId || defaultModelId(taskType),
        params: mergeGenParams(node.data.nodeType, node.data.gen?.params),
      },
    },
  }
}

function nodeDataNeedsSync(next: StudioNodeData, prev?: StudioNodeData): boolean {
  if (!prev) return true
  if (next.gen?.modelId !== prev.gen?.modelId) return true
  if (!next.audio && !prev.audio) return false
  if (!next.audio || !prev.audio) return true
  if (next.audio.activeTab !== prev.audio.activeTab) return true
  for (const tab of AUDIO_PRODUCT_TABS) {
    if (next.audio.tabs[tab]?.modelId !== prev.audio.tabs[tab]?.modelId) return true
  }
  return false
}

function computeRegisterPreview(
  registerId: string,
  nodes: StudioFlowNode[],
  edges: StudioFlowEdge[],
): string | undefined {
  const edge = edges.find((e) => e.target === registerId)
  if (!edge) return undefined
  const src = nodes.find((n) => n.id === edge.source)
  return (
    src?.data.output?.previewUrl ??
    src?.data.takes?.find((tk) => tk.version === src.data.preferredTakeVersion)?.previewUrl ??
    src?.data.takes?.[src.data.takes.length - 1]?.previewUrl ??
    src?.data.upload?.url ??
    src?.data.refCharacter?.urls?.[0] ??
    src?.data.refScene?.urls?.[0]
  )
}

function inferRegisterMediaKind(
  target: StudioFlowNode,
  upstreamUrl: string,
  nodes: StudioFlowNode[],
  edges: StudioFlowEdge[],
): string | undefined {
  const edge = edges.find((e) => e.target === target.id)
  const src = edge ? nodes.find((n) => n.id === edge.source) : undefined
  const dt = src?.data.output?.dataType
  if (dt === 'video' || dt === 'audio' || dt === 'image') return dt
  const kind = target.data.register?.kind
  if (kind === 'take_video') return 'video'
  if (kind === 'storyboard' || kind === 'take_image') return 'image'
  const lower = upstreamUrl.toLowerCase()
  if (/\.(mp4|webm|mov|m4v)(\?|#|$)/.test(lower)) return 'video'
  if (/\.(mp3|wav|ogg|m4a)(\?|#|$)/.test(lower)) return 'audio'
  return undefined
}

function buildRegisterAssetBody(
  target: StudioFlowNode,
  upstreamUrl: string,
  launch: CanvasLaunchContext,
  untitledLabel: string,
  nodes: StudioFlowNode[],
  edges: StudioFlowEdge[],
): Parameters<typeof registerProductionAsset>[1] {
  const body: Parameters<typeof registerProductionAsset>[1] = {
    url: upstreamUrl,
    name: target.data.register?.name || launch.label || untitledLabel,
    kind: target.data.register?.kind,
    source: 'canvas',
    canvas_node_id: target.id,
  }
  const mediaKind = inferRegisterMediaKind(target, upstreamUrl, nodes, edges)
  if (mediaKind) body.media_kind = mediaKind
  if (launch.ctx === 'bible-character' && launch.entityId) {
    body.bible_character_id = launch.entityId
    body.kind = 'character_ref'
  }
  if (launch.ctx === 'bible-location' && launch.entityId) {
    body.bible_location_id = launch.entityId
    body.kind = 'location_ref'
  }
  if (launch.ctx === 'bible-prop' && launch.entityId) {
    body.bible_ref_type = 'prop'
    body.bible_ref_public_id = launch.entityId
    body.kind = body.kind || 'prop'
  }
  if (launch.ctx === 'bible-costume' && launch.entityId) {
    body.bible_ref_type = 'costume'
    body.bible_ref_public_id = launch.entityId
    body.kind = body.kind || 'costume'
  }
  if (launch.ctx === 'bible-style') {
    body.bible_ref_type = 'style'
    body.bible_ref_public_id = 'default'
  }
  const registerShotId = target.data.register?.shotPublicId ?? launch.shotId
  if (registerShotId) body.shot_public_id = registerShotId
  if (launch.scriptId != null && launch.episodeNo != null && launch.sceneNo != null) {
    body.shot_scene_key = buildShotSceneScopeKey(launch.scriptId, launch.episodeNo, launch.sceneNo)
  }
  return body
}

function mimeToDataType(mime: string): HandleDataType {
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  return 'image'
}

export function CanvasPage() {
  return (
    <ReactFlowProvider>
      <CanvasThemeProvider>
        <CanvasFlowInner />
      </CanvasThemeProvider>
    </ReactFlowProvider>
  )
}
